<template>
  <div
    class="full-width"
    :style="{'border': !!errorMessage ? '2px solid red' : undefined}"
  >
    <q-editor
      ref="editor"
      :model-value="String(modelValue || '')"
      :toolbar="toolbarOptions"
      :content-class="strToObj(contentClass, {'html': true})"
      :content-style="contentStyle"
      :min-height="minHeight"
      :height="height"
      :flat="flat"
      :disable="disable"
      :placeholder="placeholder"
      square
      @update:model-value="setValue"
      @keydown.enter="onEnterKeydown"
    >
      <template #color>
        <ColorBtn
          :disable="disable"
          @run="runCommand"
        />
      </template>
      <template #clear-formatting>
        <q-btn
          dense
          no-caps
          flat
          size="sm"
          icon="format_clear"
          @click="resetFormat"
        >
          <q-tooltip>
            Clear Formatting
          </q-tooltip>
        </q-btn>
      </template>
      <template #font-sizes>
        <FontSizes
          :restricted-to-sizes="sizes"
          @run="runCommand"
        />
      </template>
      <template #image>
        <ImageUploader @run="runCommand" />
      </template>
      <template #table>
        <q-btn
          dense
          flat
          size="sm"
          label="Table"
          icon="mdi-table"
          color="black"
          @click="$refs.tableInserter.open()"
        />
      </template>
    </q-editor>
    <div
      v-if="!!errorMessage"
      class="text-red q-pa-sm bg-white"
    >
      <q-icon name="mdi-exclamation" size="1.5em" />
      {{ errorMessage }}
    </div>

    <TableInserter
      v-if="!basic"
      ref="tableInserter"
      @insert="insertTable"
    />
  </div>
</template>

<script>
import ColorBtn from './toolbars/ColorOptions'
import TableInserter from './toolbars/TableInserter'
import ImageUploader from './toolbars/ImageUploader'
import FontSizes from './toolbars/FontSizes'
import { strToObj } from 'src/helpers/globals'

/**
 * Overrides default QEditor with default toolbar options (etc)
 */
export default {
  components: {
    TableInserter, ColorBtn, ImageUploader, FontSizes
  },
  props: {
    modelValue: {
      type: String,
      default: null
    },
    // Option to view HTML code within editor
    viewSource: {
      type: Boolean,
      default: false
    },
    // Use a basic editor with a restricted toolbar of options
    basic: {
      type: Boolean,
      default: false
    },
    // List of heading sizes to display
    // Empty = show all
    sizes: {
      type: Array,
      default: () => {
        return []
      }
    },
    // Additional styles to place on the editor
    contentStyle: {
      type: [String, Object]
    },
    // Minimum editor height
    minHeight: {
      type: [String, Number]
    },
    // Fixed editor height
    height: {
      type: [String, Number]
    },
    // Classes to add to the editor
    contentClass: {
      type: [String, Object]
    },
    // Remove border around editor
    flat: {
      type: Boolean,
      default: false
    },
    disable: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String
    },
    placeholder: {
      type: String
    },
    allowLists: {
      type: Boolean,
      default: true
    },
    allowLinks: {
      type: Boolean,
      default: true
    },
    allowImages: {
      type: Boolean,
      default: false
    },
    allowFullscreen: {
      type: Boolean,
      default: false
    },
    allowMultiline: {
      type: Boolean,
      default: true
    },
    autofocus: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  computed: {
    toolbarOptions () {
      let arr = []
      if (!this.basic) {
        arr = [
          ['undo', 'redo'],
          [
            'color',
            'clear-formatting',
            'font-sizes',
            {
              label: this.$q.lang.editor.align,
              icon: this.$q.iconSet.editor.align,
              fixedLabel: true,
              list: 'only-icons',
              options: ['left', 'center', 'right', 'justify']
            }
          ]
        ]

        const formattingOptions = ['bold', 'italic', 'strike', 'underline', 'subscript', 'superscript', 'hr']
        if (this.allowLinks) {
          formattingOptions.push('link')
        }
        if (this.allowImages) {
          formattingOptions.push('image')
        }
        formattingOptions.push('table')
        arr.push(formattingOptions)

        if (this.allowLists) {
          arr.push(['unordered', 'ordered', 'outdent', 'indent'])
        }
      }
      else {
        arr = [
          ['undo', 'redo'],
          ['bold', 'italic', 'underline'],
          ['clear-formatting', 'font-sizes']
        ]
      }
      const extras = []
      if (this.viewSource) {
        extras.push('viewsource')
      }
      if (this.allowFullscreen) {
        extras.push('fullscreen')
      }
      if (extras.length > 0) {
        arr.push(extras)
      }
      return arr
    }
  },
  methods: {
    strToObj,
    runCommand (cmd, ...params) {
      const edit = this.$refs.editor
      edit.caret.restore()
      edit.runCmd(cmd, ...params)
      edit.focus()
    },
    resetFormat () {
      const edit = this.$refs.editor
      edit.runCmd('removeFormat', 'backColor')
      edit.focus()
    },
    insertTable (str) {
      this.runCommand('insertHTML', str)
    },
    setValue (val) {
      if (!val) {
        val = ''
      }
      this.$emit('update:modelValue', String(val))
    },
    onEnterKeydown (e) {
      if (!this.allowMultiline) {
        e.preventDefault()
      }
    }
  },
  mounted () {
    if (!this.modelValue) {
      this.setValue('')
    }
    if (this.autofocus) {
      this.$nextTick(() => {
        this.$refs.editor.focus()
      })
    }
  }
}
</script>
